import { Injectable } from "@angular/core";
import { Observable, BehaviorSubject } from "rxjs";
import { RiskIndicator } from "../../interfaces/riskIndicator";
import { Retrospective } from "../../interfaces/retrospective";
import { PlanningPokerParticipant } from "../../interfaces/planningPokerParticipant";

@Injectable()
export class SessionService {
  private _rememberMe: boolean = !!localStorage.getItem("token");
  private _roles: BehaviorSubject<string[]> = new BehaviorSubject<string[]>(
    JSON.parse(this.getItem("roles") || "[]")
  );
  private _subjects: Map<string, BehaviorSubject<string>> = new Map<
    string,
    BehaviorSubject<string>
  >();
  private _observables: Map<string, Observable<string>> = new Map<
    string,
    Observable<string>
  >();

  public getItem(key: string): string {
    return (this._rememberMe ? localStorage : sessionStorage).getItem(key);
  }

  public getObservable(key: string): Observable<string> {
    if (!this._subjects.has(key)) {
      this._subjects.set(key, new BehaviorSubject<string>(this.getItem(key)));
    }
    return this._subjects.get(key);
  }

  public setItem(key: string, value: string) {
    (this._rememberMe ? localStorage : sessionStorage).setItem(key, value);
    if (this._subjects.has(key)) this._subjects.get(key).next(value);
  }

  public removeItem(key: string) {
    (this._rememberMe ? localStorage : sessionStorage).removeItem(key);
  }

  setPlanningPokerStatus(status: {
    average: number;
    median: number;
    agreement: number;
    consensus: number;
    variation: number;
    selectedValue: string;
    planningOpen: boolean;
    planningClosed: boolean;
    currentFeatureId: string;
    participants: [string, PlanningPokerParticipant][];
  }) {
    this.setItem("planningPokerStatus", JSON.stringify(status));
  }

  getPlanningPokerStatus(): {
    average: number;
    median: number;
    agreement: number;
    consensus: number;
    variation: number;
    selectedValue: string;
    planningOpen: boolean;
    planningClosed: boolean;
    currentFeatureId: string;
    participants: [string, PlanningPokerParticipant][];
  } {
    let json = this.getItem("planningPokerStatus");
    return json ? JSON.parse(json) : null;
  }

  getRetrospectiveStatus(): {
    currentIndex: number;
    retrospective: Retrospective;
    indicators: RiskIndicator[];
  } {
    let json = this.getItem("retrospectiveStatus");
    return json ? JSON.parse(json) : null;
  }

  setRetrospectiveStatus(status: {
    currentIndex: number;
    retrospective: Retrospective;
    indicators: RiskIndicator[];
  }) {
    this.setItem("retrospectiveStatus", JSON.stringify(status));
  }

  deleteRetrospectiveStatus() {
    localStorage.removeItem("retrospectiveStatus");
    sessionStorage.removeItem("retrospectiveStatus");
  }

  getToken(): string {
    return this.getItem("token");
  }

  getInitials(): string {
    return this.getItem("initials");
  }

  getRoles(): Observable<string[]> {
    return this._roles;
  }

  public setRoles(roles: string[]): void {
    this._roles.next(roles);
  }

  setInitials(initials: string) {
    this.setItem("initials", initials);
  }

  public setHasActiveLicense(isActive: boolean) {
    this.setItem("hasActiveLicense", isActive ? "true" : "false");
  }

  public getHasActiveLicense(): boolean {
    const hasActiveLicense: string | undefined = this.getItem(
      "hasActiveLicense"
    );
    if (hasActiveLicense === "true") return true;
    else return false;
  }

  setToken(token: string, rememberMe: boolean, roles: string[]) {
    this._rememberMe = rememberMe;
    this._roles.next(roles);
    this.setItem("token", token);
    this.setItem("roles", JSON.stringify(roles));
  }

  destroy() {
    // Dirty hack, but we need to preserve the cookie consent variable in localstorage
    for (let i = 0; i < localStorage.length; i++) {
      if (localStorage.key(i) !== "cookie_consent_status") {
        localStorage.removeItem(localStorage.key(i));
      }
    }
    sessionStorage.clear();
    this._roles.next([]);
  }
}



// WEBPACK FOOTER //
// /agir/src/app/services/session/session.service.ts